import React, { useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { FiHome, FiShoppingCart, FiTruck, FiFileText, FiBox, FiDatabase, FiUsers, FiShield, FiSettings, FiActivity, FiBarChart2 } from 'react-icons/fi';
import { usePermissions } from '../../contexts/PermissionsContext';

interface MenuItem {
  to: string;
  icon: React.ReactNode;
  label: string;
  module: string;
  requireWrite?: boolean;
}

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const { hasRead, hasWrite } = usePermissions();
  const navigate = useNavigate();

  const mainMenu: MenuItem[] = [
    { to: '/', icon: <FiHome />, label: 'Dashboard', module: 'Dashboard' },
    { to: '/purchase-orders', icon: <FiShoppingCart />, label: 'Zamowienia', module: 'PurchaseOrders' },
    { to: '/shipments', icon: <FiTruck />, label: 'Wysylki', module: 'Shipments' },
    { to: '/invoices', icon: <FiFileText />, label: 'Faktury', module: 'Invoices' },
  ];

  const catalogMenu: MenuItem[] = [
    { to: '/products', icon: <FiBox />, label: 'Produkty', module: 'Products' },
    { to: '/inventory', icon: <FiDatabase />, label: 'Magazyn', module: 'Inventory' },
    { to: '/reports', icon: <FiBarChart2 />, label: 'Raporty', module: 'Dashboard' },
  ];

  const adminMenu: MenuItem[] = [
    { to: '/users', icon: <FiUsers />, label: 'Uzytkownicy', module: 'Users' },
    { to: '/permissions', icon: <FiShield />, label: 'Uprawnienia', module: 'Users', requireWrite: true },
    { to: '/settings/company', icon: <FiSettings />, label: 'Profil firmy', module: 'Invoices', requireWrite: true },
    { to: '/sync-logs', icon: <FiActivity />, label: 'Logi synchronizacji', module: 'Dashboard' },
  ];

  const handleLinkClick = useCallback(
    (to: string) => {
      navigate(to);
      onClose();
    },
    [navigate, onClose]
  );

  const renderLink = (item: MenuItem) => {
    if (item.requireWrite ? !hasWrite(item.module as any) : !hasRead(item.module as any)) {
      return null;
    }
    return (
      <NavLink
        key={item.to}
        to={item.to}
        end={item.to === '/'}
        className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
        onClick={(e) => {
          e.preventDefault();
          handleLinkClick(item.to);
        }}
      >
        <span className="icon">{item.icon}</span>
        <span className="sidebar-link-label">{item.label}</span>
      </NavLink>
    );
  };

  return (
    <aside className={`sidebar ${isOpen ? 'sidebar--open' : ''}`}>
      <div className="sidebar-header">
        <div className="sidebar-logo">AK</div>
        <div className="sidebar-header-text">
          <div className="sidebar-title">AmazonKrosno</div>
          <div className="sidebar-subtitle">Panel dostawcy</div>
        </div>
      </div>

      <nav className="sidebar-nav">
        <div className="sidebar-section">
          <div className="sidebar-section-title">Menu</div>
          {mainMenu.map(renderLink)}
        </div>

        <div className="sidebar-section">
          <div className="sidebar-section-title">Katalog</div>
          {catalogMenu.map(renderLink)}
        </div>

        {(hasRead('Users' as any)) && (
          <div className="sidebar-section">
            <div className="sidebar-section-title">Administracja</div>
            {adminMenu.map(renderLink)}
          </div>
        )}
      </nav>
    </aside>
  );
};

export default Sidebar;
